import {
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    inject,
    Injectable,
    Injector,
    NgZone,
    RendererFactory2,
    DOCUMENT,
} from '@angular/core';

import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { MetrixConfiguratorDynamicComponent } from '../components/metrix-configurator-dynamic/metrix-configurator-dynamic.component';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';

interface BridgeEvent extends Event {
    detail: {
        bridge: Bridge;
    };
}

interface Bridge {
    currentLanguage: string;
    setContextValue(key: string, value: string): void;
    initContainer(v1?: null | string, v2?: null | string): void;
    listen(event: string, fun: ButtonClicked): void;
    integration: {
        mode(mode: string): string;
        loadSnapshot(id: string, mode: string | undefined): Promise<string>;
    };
}

type ButtonClicked = (
    event: CustomEvent<{
        button: string;
        containerId: string;
    }>,
) => void;

@Injectable({
    providedIn: 'root',
})
export class MetrixInitService {
    private rendererFactory2 = inject(RendererFactory2);
    private componentFactoryResolver = inject(ComponentFactoryResolver);
    private injector = inject(Injector);
    private applicationRef = inject(ApplicationRef);
    private translateService = inject(TranslateService);
    private ngZone = inject(NgZone);
    private document = inject(DOCUMENT);

    private renderer2 = this.rendererFactory2.createRenderer(null, null);

    initialized?: boolean;
    bridgeInitialized$ = new BehaviorSubject(false);
    bridge?: Bridge;
    snapshotIdEvent$ = new BehaviorSubject<null | string>(null);
    createOrder$ = new Subject<string>();
    euroRate = 4;

    private componentRef?: ComponentRef<MetrixConfiguratorDynamicComponent>;

    append(elementRef: ElementRef): void {
        if (!this.initialized) {
            this.initialized = true;
            this.createComponent();
            this.attacheComponent(elementRef);
            this.appendScripts();
            this.document.addEventListener('metrix:configuratorInitialized', (bridge: BridgeEvent) => {
                this.bridge = bridge.detail.bridge;
                this.bridgeInitialized$.next(true);

                this.bridge.currentLanguage = this.translateService.currentLang;

                switch (this.translateService.currentLang) {
                    case 'pl':
                        this.bridge.setContextValue('currency', 'PLN');
                        break;
                    default:
                        this.bridge.setContextValue('currency', 'EUR');
                        break;
                }
                if (this.snapshotIdEvent$.value === null) {
                    this.bridge.initContainer(null, null);
                }
                this.bridge.listen('buttonClicked', this.bridgeButtonClicked);
            });

            this.watchForSnapshotId();
        } else {
            this.attacheComponent(elementRef);

            this.bridge?.initContainer(null, null);
        }
    }

    detache(elementRef: ElementRef): void {
        const container = elementRef.nativeElement.querySelector('.metrix-configurator-container');
        this.renderer2.removeChild(container, this.componentRef?.location.nativeElement);
    }

    reset(): void {
        if (this.bridge) {
            this.bridge.initContainer(null, null);
        }
    }

    load(snapshotId: string): void {
        this.snapshotIdEvent$.next(snapshotId);
    }

    private bridgeButtonClicked = (event: CustomEvent<{ button: string; containerId: string }>): void => {
        this.ngZone.run(() => {
            switch (event.detail.button) {
                case 'addToCart':
                case 'print':
                    this.createOrder$.next(event.detail.containerId);
                    break;
                default:
                    console.log(`Unsupported action: ${event.detail.button}`);
                    break;
            }
        });
    };

    private appendScripts(): void {
        const script = this.renderer2.createElement('script');
        script['src'] = `${environment.metrixUrl}integration.js`;
        script['type'] = `text/javascript`;
        script['defer'] = true;

        this.renderer2.appendChild(this.document.body, script);
    }

    private createComponent(): void {
        this.componentRef = this.componentFactoryResolver.resolveComponentFactory(MetrixConfiguratorDynamicComponent).create(this.injector);
        this.applicationRef.attachView(this.componentRef.hostView);
    }

    private attacheComponent(elementRef: ElementRef): void {
        const container = elementRef.nativeElement.querySelector('.metrix-configurator-container');
        this.renderer2.appendChild(container, this.componentRef?.location.nativeElement);
    }

    private watchForSnapshotId() {
        combineLatest([this.snapshotIdEvent$.pipe(filter((data) => data !== null)), this.bridgeInitialized$.pipe(filter((value) => value === true))])
            .pipe(
                tap(([id]) => {
                    const mode = this.bridge?.integration.mode('configurator');

                    this.bridge?.integration.loadSnapshot(id, mode).then(
                        (containerId) => {
                            this.bridge?.initContainer(mode, containerId);
                        },
                        () => {
                            this.bridge?.initContainer(null, null);
                        },
                    );
                }),
            )
            .subscribe();
    }
}
